import { useEffect, useState } from "react";
import { GlassPanel } from "@/components/ui/glass-panel";
import { History, TrendingUp, TrendingDown, Award } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocation } from "@/components/location-provider";
import { useUnit } from "@/components/unit-provider";

interface HistoricalResponse {
  date: string;
  years: number;
  todayMaxC?: number;
  todayMinC?: number;
  history: { year: number; maxC: number | null; minC: number | null; precipMm: number | null }[];
  maxRankPct: number | null;
  minRankPct: number | null;
  avgMaxC: number | null;
  avgMinC: number | null;
  recordHigh: { year: number; tempC: number } | null;
  recordLow: { year: number; tempC: number } | null;
}

interface HistoricalComparisonProps {
  todayMaxC?: number;
  todayMinC?: number;
}

function rankNarrative(rank: number | null, kind: "max" | "min"): string {
  if (rank == null) return "";
  // For max: rank=80 means today is hotter than 80% of past years.
  if (kind === "max") {
    if (rank >= 95) return `record-warm — hotter than ${rank}% of years on this date`;
    if (rank >= 80) return `notably warm — hotter than ${rank}% of years`;
    if (rank >= 60) return `a bit warmer than usual (${rank}th percentile)`;
    if (rank >= 40) return `near the historical average`;
    if (rank >= 20) return `a bit cooler than usual (${rank}th percentile)`;
    if (rank >= 5) return `notably cool — colder than ${100 - rank}% of years`;
    return `record-cool — colder than ${100 - rank}% of years on this date`;
  }
  // For min: same rank meaning but flipped semantics for cold-night reading.
  if (rank >= 95) return `unusually mild night for this date (warmer than ${rank}% of years)`;
  if (rank >= 80) return `mild night (warmer than ${rank}% of years)`;
  if (rank >= 40) return `near the historical low`;
  if (rank >= 20) return `cold night (colder than ${100 - rank}% of years)`;
  return `extremely cold night for this date`;
}

export function HistoricalComparison({ todayMaxC, todayMinC }: HistoricalComparisonProps) {
  const [data, setData] = useState<HistoricalResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { current: location } = useLocation();
  const { display } = useUnit();

  useEffect(() => {
    if (todayMaxC == null && todayMinC == null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    const params = new URLSearchParams({
      lat: String(location.lat),
      lon: String(location.lon),
    });
    if (todayMaxC != null) params.set("todayMax", String(todayMaxC));
    if (todayMinC != null) params.set("todayMin", String(todayMinC));
    fetch(`/api/historical?${params.toString()}`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [location.lat, location.lon, todayMaxC, todayMinC]);

  if (loading) {
    return (
      <GlassPanel intensity="low" className="p-4">
        <div className="h-40 animate-pulse rounded-xl bg-muted/30" />
      </GlassPanel>
    );
  }

  if (error || !data || data.years === 0) {
    return null;
  }

  // Sort history chronologically and find min/max for the bar chart.
  const sorted = [...data.history].sort((a, b) => a.year - b.year);
  const allMax = sorted.map((h) => h.maxC).filter((v): v is number => v != null);
  const allMin = sorted.map((h) => h.minC).filter((v): v is number => v != null);
  const yMax = Math.max(...allMax, todayMaxC ?? -Infinity, data.recordHigh?.tempC ?? -Infinity);
  const yMin = Math.min(...allMin, todayMinC ?? Infinity, data.recordLow?.tempC ?? Infinity);
  const span = yMax - yMin || 1;

  function position(t: number): number {
    return ((yMax - t) / span) * 100;
  }

  const maxRankColor =
    data.maxRankPct == null
      ? "text-muted-foreground"
      : data.maxRankPct >= 80
        ? "text-orange-400"
        : data.maxRankPct >= 60
          ? "text-amber-400"
          : data.maxRankPct >= 40
            ? "text-emerald-400"
            : data.maxRankPct >= 20
              ? "text-sky-400"
              : "text-blue-400";

  const dateLabel = (() => {
    const [m, d] = data.date.split("-").map(Number);
    return new Date(2000, m - 1, d).toLocaleDateString([], {
      month: "long",
      day: "numeric",
    });
  })();

  return (
    <GlassPanel intensity="low" className="p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
            <History className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-bold tracking-tight">Climate Comparison</h3>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {dateLabel} · {data.years}-year archive
            </p>
          </div>
        </div>
      </div>

      {/* Headline ranks */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        {todayMaxC != null && data.maxRankPct != null && (
          <div className="rounded-xl border border-border/40 bg-background/30 p-3">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold inline-flex items-center gap-1">
                <TrendingUp className="h-3 w-3 text-orange-400" />
                Today's high
              </span>
              <span className={cn("text-base font-bold tabular-nums", maxRankColor)}>
                {data.maxRankPct}
                <span className="text-[10px] font-normal text-muted-foreground ml-0.5">th %ile</span>
              </span>
            </div>
            <p className="text-sm font-semibold">
              {display(todayMaxC)}
              {data.avgMaxC != null && (
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  vs avg {display(data.avgMaxC)}
                </span>
              )}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5 capitalize leading-snug">
              {rankNarrative(data.maxRankPct, "max")}
            </p>
          </div>
        )}

        {todayMinC != null && data.minRankPct != null && (
          <div className="rounded-xl border border-border/40 bg-background/30 p-3">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold inline-flex items-center gap-1">
                <TrendingDown className="h-3 w-3 text-sky-400" />
                Today's low
              </span>
              <span className="text-base font-bold tabular-nums text-foreground/80">
                {data.minRankPct}
                <span className="text-[10px] font-normal text-muted-foreground ml-0.5">th %ile</span>
              </span>
            </div>
            <p className="text-sm font-semibold">
              {display(todayMinC)}
              {data.avgMinC != null && (
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  vs avg {display(data.avgMinC)}
                </span>
              )}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5 capitalize leading-snug">
              {rankNarrative(data.minRankPct, "min")}
            </p>
          </div>
        )}
      </div>

      {/* History bar chart of yearly highs */}
      <div className="rounded-xl border border-border/40 bg-background/30 p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            Yearly highs on {dateLabel}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {sorted[0]?.year} – {sorted[sorted.length - 1]?.year}
          </p>
        </div>
        <div className="relative h-24 flex items-end gap-[2px]">
          {sorted.map((h) => {
            const v = h.maxC;
            if (v == null) {
              return <div key={h.year} className="flex-1" />;
            }
            const top = position(v);
            const heightPct = 100 - top;
            const isRecord = data.recordHigh?.year === h.year;
            return (
              <div
                key={h.year}
                className="flex-1 flex flex-col justify-end h-full group/bar relative"
                title={`${h.year}: ${v.toFixed(1)}°C`}
              >
                <div
                  className={cn(
                    "w-full rounded-t-sm transition-all",
                    isRecord
                      ? "bg-orange-400"
                      : "bg-primary/40 group-hover/bar:bg-primary/70",
                  )}
                  style={{ height: `${Math.max(4, heightPct)}%` }}
                />
              </div>
            );
          })}

          {/* Today's high marker line */}
          {todayMaxC != null && (
            <div
              className="absolute left-0 right-0 border-t-2 border-dashed border-emerald-400/80"
              style={{ top: `${position(todayMaxC)}%` }}
            >
              <span className="absolute -top-4 right-0 text-[10px] font-bold text-emerald-300 bg-background/80 px-1 rounded">
                Today {display(todayMaxC)}
              </span>
            </div>
          )}
          {/* Average line */}
          {data.avgMaxC != null && (
            <div
              className="absolute left-0 right-0 border-t border-dotted border-white/30"
              style={{ top: `${position(data.avgMaxC)}%` }}
            >
              <span className="absolute -top-4 left-0 text-[10px] text-muted-foreground bg-background/80 px-1 rounded">
                Avg {display(data.avgMaxC)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Records */}
      {(data.recordHigh || data.recordLow) && (
        <div className="grid grid-cols-2 gap-2 mt-3">
          {data.recordHigh && (
            <div className="rounded-lg bg-orange-500/10 border border-orange-500/20 px-2.5 py-1.5 flex items-center gap-2">
              <Award className="h-3.5 w-3.5 text-orange-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Record high
                </p>
                <p className="text-xs font-bold tabular-nums">
                  {display(data.recordHigh.tempC)}
                  <span className="ml-1 text-[10px] font-normal text-muted-foreground">
                    in {data.recordHigh.year}
                  </span>
                </p>
              </div>
            </div>
          )}
          {data.recordLow && (
            <div className="rounded-lg bg-sky-500/10 border border-sky-500/20 px-2.5 py-1.5 flex items-center gap-2">
              <Award className="h-3.5 w-3.5 text-sky-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Record low
                </p>
                <p className="text-xs font-bold tabular-nums">
                  {display(data.recordLow.tempC)}
                  <span className="ml-1 text-[10px] font-normal text-muted-foreground">
                    in {data.recordLow.year}
                  </span>
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </GlassPanel>
  );
}
